import { taskAxiosInstance } from "../axiosInstance";
import type { Task } from "./taskApi";
import type { TaskGroup, TaskGroupWithTasks } from "./taskGroupApi";
import type { TaskOccurrence } from "./taskOccurrenceApi";
import type { TaskAction, TaskStatus } from "./types";

export interface TaskList {
    id: number;
    instanceId: number;
    title: string;
    description: string | null;
    template: boolean;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    responsibleUserId: number | null;
    createdById: number;
    updatedById: number;
}

export interface TaskListWithDetails extends TaskList {
    tasks: Task[];
    groups: TaskGroupWithTasks[];
}

export interface TaskWithOccurrences extends Task {
    occurrences: TaskOccurrence[];
}

export interface TaskGroupWithTaskOccurrences extends TaskGroup {
    tasks: TaskWithOccurrences[];
}

export interface TaskListWithTaskOccurrences extends TaskList {
    responsibleUser: { id: number; firstName: string; lastName: string } | null;
    tasks: TaskWithOccurrences[];
    groups: TaskGroupWithTaskOccurrences[];
}

export interface CreateTaskForListInput {
    title: string;
    description?: string | null;
    sourceApp?: string;
    action?: TaskAction;
    annualCycle?: boolean;
    ownerUserId?: number;
    status?: TaskStatus;
    rrule?: string | null;
    duration?: number;
}

export interface CreateGroupForListInput {
    title: string;
    tasks?: CreateTaskForListInput[];
}

export interface CreateTaskListInput {
    instanceId: number;
    title: string;
    description?: string | null;
    template?: boolean;
    responsibleUserId?: number | null;
    tasks?: CreateTaskForListInput[];
    groups?: CreateGroupForListInput[];
}

export type UpdateTaskListInput = Partial<CreateTaskListInput>;

export interface UpdateTaskInputForList {
    id?: number;
    title: string;
    description?: string | null;
}

export interface UpdateGroupInputForList {
    id?: number;
    title: string;
    tasks?: UpdateTaskInputForList[];
}

export interface UpdateTaskListWithDetailsInput {
    title?: string;
    description?: string | null;
    template?: boolean;
    responsibleUserId?: number | null;
    tasks?: UpdateTaskInputForList[];
    groups?: UpdateGroupInputForList[];
}

export interface CreateTaskListWithDetailsResponse {
    list: TaskList;
    groups: TaskGroup[];
    tasks: Task[];
}

export interface TaskListQuery {
    instanceId?: number;
}

export const getTaskLists = async (query: TaskListQuery = {}): Promise<TaskList[]> => {
    const params = new URLSearchParams();
    if (query.instanceId !== undefined) params.set("instanceId", query.instanceId.toString());

    const queryString = params.toString();
    const response = (
        await taskAxiosInstance.get<TaskList[]>(
            `/task-lists${queryString ? `?${queryString}` : ""}`,
        )
    ).data;
    return response;
};

export const getTaskListById = async (id: number): Promise<TaskList> => {
    const response = (await taskAxiosInstance.get<TaskList>(`/task-lists/${id}`)).data;
    return response;
};

export const getTaskListWithTasks = async (id: number): Promise<TaskListWithTaskOccurrences> => {
    const response = (
        await taskAxiosInstance.get<TaskListWithTaskOccurrences>(`/task-lists/${id}/with-tasks`)
    ).data;
    return response;
};

export const createTaskList = async (
    data: CreateTaskListInput,
): Promise<CreateTaskListWithDetailsResponse> => {
    const response = (
        await taskAxiosInstance.post<CreateTaskListWithDetailsResponse>(
            "/task-lists/with-details",
            data,
        )
    ).data;
    return response;
};

export const updateTaskList = async (id: number, data: UpdateTaskListInput): Promise<TaskList> => {
    const response = (await taskAxiosInstance.patch<TaskList>(`/task-lists/${id}`, data)).data;
    return response;
};

export const updateTaskListWithDetails = async (
    id: number,
    data: UpdateTaskListWithDetailsInput,
): Promise<CreateTaskListWithDetailsResponse> => {
    const response = (
        await taskAxiosInstance.patch<CreateTaskListWithDetailsResponse>(
            `/task-lists/${id}/with-details`,
            data,
        )
    ).data;
    return response;
};

export const deleteTaskList = async (id: number): Promise<TaskList> => {
    const response = (await taskAxiosInstance.post<TaskList>(`/task-lists/${id}/delete`)).data;
    return response;
};

export const logTaskListPrint = async (id: number): Promise<void> => {
    await taskAxiosInstance.post(`/task-lists/${id}/print`);
};

export interface ListsWithTasksQuery {
    count?: number;
    instanceId?: number;
    template?: boolean;
}

export const getListsWithTasks = async (
    query: ListsWithTasksQuery = {},
): Promise<TaskListWithTaskOccurrences[]> => {
    const params = new URLSearchParams();
    if (query.count) {
        params.append("count", query.count.toString());
    }
    if (query.instanceId) {
        params.append("instanceId", query.instanceId.toString());
    }
    if (query.template !== undefined) {
        params.append("template", String(query.template));
    }

    const queryString = params.toString();
    const response = await taskAxiosInstance.get<TaskListWithTaskOccurrences[]>(
        `/task-lists/with-tasks${queryString ? `?${queryString}` : ""}`,
    );

    return response.data;
};
